'use client';

import type { FC } from 'react';
import Link from 'next/link';
import { MoveDownRight } from 'lucide-react';

import type { TBoardLaneKind, TBoardRailLink } from '@/utils/auctions/auctionBoard';

/**
 * The last card of a lane: where the rest of it lives (spec 014, owner
 * feedback 2026-09-07).
 *
 * A card rather than a smarter arrow: an arrow that scrolls nine times and
 * navigates on the tenth is a control that changes meaning, and people
 * double-press arrows. A final tile the size of the cars snaps into place like
 * the rest, works under a thumb where there is no arrow at all, and says in
 * words where it leads.
 *
 * It has the tiles' anatomy on purpose — the same 3:4 box with the same bottom
 * cut, the same text block — so it reads as the last car in the row rather
 * than a placeholder. The box stays dark — a gold box shouted next to the
 * photos — with the seam's stripes as a faint gold grain, and the gold is kept
 * for the round arrow the gallery tiles carry, grown up, and the fact line.
 */

/*
 * What the tile promises is where the LINK goes, not what this lane holds. The
 * sold lane is still a "what just happened" strip of the last few days (spec
 * 014, FR-012 of spec 021); the listing it opens now reaches six months back,
 * and saying "Ostatni miesiąc" there would undersell it and contradict the page
 * the visitor lands on.
 */
const FACT: Record<TBoardLaneKind, string> = {
  sold: 'Pół roku sprzedaży',
  upcoming: 'Pełna lista z filtrami',
};

export const AuctionBoardMoreTile: FC<{ lane: TBoardLaneKind; link: TBoardRailLink }> = ({ lane, link }) => (
  <Link
    href={link.href}
    data-lane={lane}
    data-id="more"
    className="group block h-full w-[220px] overflow-hidden rounded-xl border border-neutral-800 bg-backgroundLight transition-[border-color,transform,box-shadow] duration-300 hover:border-action/60 hover:shadow-golden-glow motion-safe:hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background md:w-[256px]"
  >
    <div className="relative aspect-[3/4] w-full overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 to-background [clip-path:polygon(0_0,100%_0,100%_86%,0_100%)]">
        {/* The seam's angle, as a faint gold grain on the dark. */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] [background-image:repeating-linear-gradient(-70deg,transparent_0,transparent_18px,rgb(251_192_11)_18px,rgb(251_192_11)_20px)]"
        />
        <div className="pointer-events-none absolute inset-0 bg-automotive-gradient opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        <span className="absolute left-4 top-4 font-oswald text-[11px] uppercase tracking-[0.3em] text-neutral-500">
          Copart · IAAI
        </span>

        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full border border-action bg-action text-background shadow-golden-glow transition-transform duration-300 motion-safe:group-hover:scale-110">
            <MoveDownRight className="h-8 w-8 -rotate-90" aria-hidden />
          </span>
        </span>
      </div>
    </div>

    <div className="relative flex h-[6.5rem] flex-col px-4 pb-4 pt-1">
      <span className="font-oswald text-xs uppercase tracking-[0.25em] text-neutral-500">Zobacz więcej</span>
      <span className="mt-0.5 line-clamp-2 font-oswald text-lg uppercase leading-tight text-foreground transition-colors group-hover:text-action">
        {link.label}
      </span>
      <span className="mt-auto truncate pt-1 font-oswald text-xs uppercase tracking-[0.2em] text-action">{FACT[lane]}</span>
    </div>
  </Link>
);
